import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translation content from JSON files
import hiContent from './translations/hi_content.json';
import heContent from './translations/he_content.json';
import jaContent from './translations/ja_content.json';

// English content with full arrays
const enContent = {
  coreProductClasses: {
    title: 'Delivered System Classes (Representative) — Built-to-Spec Capability',
    subtitle: 'HADEN delivers program-critical electromechanical systems for defense environments. The system classes below summarize delivered capability ranges and the core engineering building blocks typically embedded inside each system class.',
    footnote: 'Representative ranges shown. Exact architecture, verification scope, and qualification evidence are program-dependent.',
    deliveredProductsLabel: 'DELIVERED PRODUCT EXAMPLES',
    deployedForPrefix: 'Deployed for',
    deliveredProducts: [
      ['Shelter Cooling Units (SCU)', 'Liquid Cooling Units (LCU)', 'Air Ventilation & Conditioning Systems', 'Electronics Cooling Packages'],
      ['Pressure Line Assemblies', 'Flow Regulation Skids', 'Liquid-to-Liquid Heat Exchangers', 'Coolant Distribution Networks'],
      ['Power Distribution Units (PDU)', 'Motion Control Panels', 'Switchgear & Protection Systems', 'Defense-Grade Wiring Harnesses'],
      ['Gas Regulation Assemblies', 'Pressure Reduction Stations', 'Purge & Charging Systems', 'Safety Interlock Panels']
    ],
    deployedFor: [
      'Deployed for radar systems, command posts, mobile shelters, and shipboard electronics',
      'Deployed for naval platforms, ground support systems, and test facilities',
      'Deployed for radar vans, mobile launchers, shipboard systems, and ground stations',
      'Deployed for missile support, test stands, propulsion systems, and calibration rigs'
    ],
    tiles: [
      {
        kicker: '1–660 kW class (representative)',
        title: 'Thermal Management Systems',
        oneLiner: 'Vapor-compression and liquid-cooling architectures engineered for continuous duty, stable control, and acceptance-ready verification.',
        bullets: [
          'Compressor staging strategy + electronic expansion valve (EEV) control + superheat/subcooling stability design',
          'Heat exchanger and airflow architecture (condenser/evaporator) engineered for varying ambient/heat-load conditions',
          'Instrumentation, safety interlocks, and control logic aligned to predictable FAT/ATP results and evidence closure',
          'Serviceability-aware packaging: access planning, maintainability provisions, and fault-isolation approach (program dependent)'
        ]
      },
      {
        kicker: 'Distribution skids + piping networks',
        title: 'Hydronic (Liquid Circulation) Networks',
        oneLiner: 'Conditioned fluid distribution engineered for controlled flow, pressure stability, reliability, and maintainable field operation.',
        bullets: [
          'Pump sizing and NPSH discipline + flow balancing + manifold architecture (supply/return)',
          'Filtration strategy + instrumentation integration (pressure/flow/temperature) and health monitoring provisions',
          'ΔP management, isolation/bypass logic, and controlled coupling interfaces for platform integration',
          'Leak-prevention design discipline: service access discipline, sealing strategy, and maintainability-first layouts'
        ]
      },
      {
        kicker: 'Servo + PLC/HMI systems (representative)',
        title: 'Electrical Panels & Motion Control (with SCADA Health Layer)',
        oneLiner: 'Panels engineered for traceable wiring, protection reliability, and field diagnostics — including motion control where applicable.',
        bullets: [
          'Servo azimuth/elevation and motion-control architectures (program dependent), with safety logic and interlocks',
          'Protection, grounding/EMI philosophy, and maintainable wiring discipline aligned to field reliability',
          'SCADA/HMI as the "eyes of the system": condition monitoring, state visibility, alarms/events, and diagnostics for maintainability and availability',
          'In-house panel build and integration discipline; aerospace/defense-grade harnessing capability deliverable where required'
        ]
      },
      {
        kicker: 'Up to 300 bar class (representative)',
        title: 'High-Pressure Regulation Systems',
        oneLiner: 'Regulated pneumatic assemblies engineered for predictable pressure delivery, safety logic, and validation-driven acceptance.',
        bullets: [
          'Regulation strategy including relief approach, controlled set-point behavior, and fail-safe logic (program dependent)',
          'Cleanliness/contamination control discipline and leak-integrity design embedded into build execution',
          'Test and validation discipline aligned to measurable acceptance criteria and evidence readiness',
          'Serviceability planning: maintainable layouts, isolation strategy, and safe handling provisions'
        ]
      }
    ]
  },
  hero: {
    requestQuote: 'Request Quote',
    technicalDiscussion: 'Technical Discussion',
    headline_line1: 'Military-Grade',
    headline_line2: 'Build-to-Specification',
    headline_line3: 'Engineering & Manufacturing',
    subheadline: 'Customer requirements translated into validated, acceptance-ready systems.',
    supporting_text: 'Beyond customer specifications, HADEN applies proven, program-dependent military engineering disciplines—environmental readiness, EMI/EMC integration, Reliability & Maintainability (RAM), and human engineering—so the design survives real-world deployment and closes acceptance cleanly.'
  },
  milComplianceChips: [
    'MIL-STD-810 alignment (as applicable)',
    'MIL-STD-461 alignment (as applicable)',
    'RAM engineering discipline'
  ],
  proofChips: [
    '1–660 kW class thermal systems',
    'Multi-service deployments + export deliveries',
    'Configuration-controlled builds',
    'FAT/ATP evidence-pack readiness',
    'Multi-kiloamp power distribution & control',
    '300 bar-class regulated pressurization systems'
  ],
  milGradeLayer: {
    title: 'MIL-Grade Engineering Layer',
    subtitle: '(Program Dependent)',
    microLine: 'Program-dependent military engineering alignment is embedded during requirements translation — not added at the end.',
    footnote: 'Exact standards alignment and verification scope depend on customer program requirements and contractual documentation.',
    pillars: [
      {
        title: 'Environmental Readiness',
        description: 'MIL-STD-810 alignment for shock, vibration, temperature, humidity—applied as program specifications demand.'
      },
      {
        title: 'EMI/EMC Integration',
        description: 'MIL-STD-461 alignment for electromagnetic compatibility—integrated into the build process when required.'
      },
      {
        title: 'RAM Engineering',
        description: 'Reliability, Availability & Maintainability discipline—designed into the system from day one.'
      },
      {
        title: 'Human Engineering',
        description: 'Ergonomics, accessibility, and operator safety—engineered for real-world field conditions.'
      }
    ]
  },
  programLifecycle: {
    title: 'Program Lifecycle Support',
    subtitle: 'From specification translation to witness-ready acceptance — executed with controlled baselines, traceability, and evidence closure.',
    cards: [
      {
        title: 'Specification Translation & System Engineering',
        description: 'Customer specifications are decomposed into interfaces, derived constraints, and acceptance criteria—so engineering starts from a controlled baseline.',
        signals: ['Requirement closure matrix', 'Assumptions control', 'Interface definition']
      },
      {
        title: 'Prototype-to-Production Readiness',
        description: 'Prototype builds are executed with controlled configuration so production transition is predictable, repeatable, and acceptance-friendly.',
        signals: ['As-built traceability', 'Rework control', 'Manufacturing readiness discipline']
      },
      {
        title: 'FAT/ATP & Witness Readiness',
        description: 'Acceptance planning is organized as an evidence pack—test intent, setup readiness, results reporting—aligned to closure criteria.',
        signals: ['ATP/TP', 'FAT readiness checklist', 'Witness testing support']
      }
    ]
  },
  enablers: {
    title: 'In-House Engineering Enablers (How Delivery Stays Predictable)',
    subtitle: 'Delivered systems are enabled by disciplined translation of customer requirements into controlled design, controlled build, and controlled evidence — with traceability retained from requirement interpretation through acceptance readiness.',
    offeredVariants: {
      title: 'Offered Variants (Program Dependent)',
      subtext: 'Beyond representative delivered configurations, HADEN engineers these system classes across multiple architectures and deployment constraints—tailored to program acceptance criteria.',
      footnote: 'Exact configuration, qualification standards alignment, and evidence scope are governed by customer program requirements and contractual documentation.',
      items: [
        {
          category: 'Thermal Management Systems',
          details: 'liquid-to-liquid, liquid-to-refrigerant, air-to-refrigerant, conditioned air delivery for controlled environments (application dependent)'
        },
        {
          category: 'Hydronic Circulation Networks',
          details: 'single-loop and multi-branch distribution skids, filtration and flow conditioning, field-deployable piping networks, seawater interfacing where required (program dependent)'
        },
        {
          category: 'Electrical Panels & Motion Control',
          details: 'power distribution and protection panels, servo motion-control panels, PLC/HMI + SCADA health monitoring layer, aerospace/defense-grade harnessing deliverables (as applicable)'
        },
        {
          category: 'High-Pressure Regulation Systems',
          details: 'regulation and distribution assemblies, purge/charging and controlled pressurization logic, safety interlocks, leak-integrity validation support (application dependent)'
        }
      ]
    },
    cards: [
      {
        name: 'Spec Translation & Architecture Visualization',
        oneLiner: 'Requirements are matured into a coherent system mental model before CAD execution, reducing late churn and improving acceptance predictability.',
        bullets: [
          'Deep requirement interpretation + usability thinking beyond literal spec text (program dependent)',
          'Architecture formation before tool-driven detailing, enabling cleaner integration decisions',
          'CAD modeling used to lock interfaces, service access logic, and manufacturability intent early'
        ]
      },
      {
        name: 'CAD-to-CAM Manufacturing Readiness',
        oneLiner: 'Manufacturing is executed with controlled workmanship intent — enabling repeatability, traceability, and predictable integration.',
        bullets: [
          'CAM-aligned fabrication planning, fitment discipline, and controlled build practices',
          'Workmanship controls: routing, access planning, mounting integrity, and serviceability provisions',
          'Build traceability retained for as-built configuration summary and acceptance readiness'
        ]
      },
      {
        name: 'Structures, Sheet Metal & System Packaging',
        oneLiner: 'Enclosures, frames, skids, and housings are produced in-house to maintain dimensional control and integration readiness.',
        bullets: [
          'Packaging engineered for transport, handling, and field constraints (program dependent)',
          'Maintainability-first layouts with access discipline and protection intent',
          'Integration-ready fabrication supporting predictable assembly and testing'
        ]
      },
      {
        name: 'Harnessing & Electrical Workmanship Control',
        oneLiner: 'Wiring and harnessing are executed with traceable workmanship discipline aligned to field reliability.',
        bullets: [
          'Harness design and build discipline (routing, identification, workmanship controls)',
          'Protection and grounding philosophy aligned to EMI/EMC readiness (as applicable)',
          'Testability provisions embedded for diagnostics and verification evidence'
        ]
      },
      {
        name: 'HADEN ATLAS — Requirements-to-Baseline Traceability',
        oneLiner: 'Execution is coordinated through an AS9100D-aligned digital backbone that links customer requirements to controlled engineering definition and released baselines.',
        bullets: [
          'Requirement capture and decomposition (spec → customer requirement table/CR structure) with traceability retained',
          'Engineering definition linkage (requirements → design intent → bill of materials) with controlled baselines',
          'Planning and procurement traceability supporting repeatable execution and configuration integrity',
          'Documentation linkage supporting acceptance readiness (as applicable: drawings, lists, plans)'
        ]
      },
      {
        name: 'HADEN ATLAS — Change Control & Evidence Pack Readiness',
        oneLiner: 'Configuration changes and acceptance evidence are controlled so as-built integrity and closure artifacts remain audit-ready and program-aligned.',
        bullets: [
          'Change control discipline with configuration traceability (ECR/ECO flows, approvals, baselines)',
          'As-built configuration integrity (what changed, why, and how it was closed)',
          'Evidence pack readiness support (test logs, verification closure artifacts, traceability snapshots)',
          'Record discipline aligned to AS9100D expectations (retention and retrievability as applicable)'
        ]
      }
    ]
  },
  confidentialityNote: {
    text: 'Confidential by default.',
    subtext: 'Program names, reference letters, and product visuals are shared only with verified stakeholders through a secure technical discussion.',
    ctaText: 'Request Technical Briefing'
  },
  executionFlow: {
    title: 'How HADEN Executes Build-to-Specification Programs',
    subtitle: 'Programs are executed with disciplined translation of requirements into design controls, build controls, and verification evidence — so acceptance is predictable, not last-minute.',
    steps: [
      {
        title: 'Requirement Intake & Decomposition',
        description: 'Customer specifications are decomposed into derived requirements, interface definitions, and acceptance criteria.',
        points: ['Specification decomposition', 'Assumptions log', 'Acceptance linkage']
      },
      {
        title: 'Design & Configuration Control',
        description: 'Design outputs are controlled under baseline management so changes are tracked and impact is assessed.',
        points: ['Baseline management', 'Change control', 'Interface freeze']
      },
      {
        title: 'Build Execution & Traceability',
        description: 'Build activities follow controlled procedures with as-built documentation and non-conformance control.',
        points: ['As-built records', 'NCR control', 'Inspection holds']
      },
      {
        title: 'Verification & Evidence Closure',
        description: 'Test planning is aligned to acceptance criteria; results are documented for closure.',
        points: ['Test planning', 'Evidence pack', 'Closure matrix']
      }
    ]
  },
  evidencePack: {
    title: 'Typical Evidence Pack',
    label: 'Typical Acceptance Evidence Pack (program dependent):',
    microLine: 'Evidence is structured to support acceptance closure without exposing sensitive program details.',
    chips: [
      'Requirement Traceability Matrix (RTM)',
      'Design Review Records',
      'As-Built Configuration Summary',
      'Test Procedures (ATP/TP)',
      'FAT Results & NCR Closure',
      'Handover Documentation'
    ]
  },
  proofPoints: {
    title: 'Track Record',
    subtitle: 'Reliability backed by execution',
    stats: { years: 'Years Experience', systems: 'Systems Delivered', documentation: 'FAT-Ready Documentation' },
    executionDiscipline: {
      title: 'Execution Discipline (AS9100D-Aligned)',
      items: [
        'End-to-end program execution: specification translation → build → verification → handover closure',
        'Configuration-controlled builds with traceable as-built documentation (program dependent)',
        'Nonconformance control with closure ownership (containment → root cause → corrective action)',
        'Sustainment mindset: spares planning, change impact assessment, and documentation continuity'
      ]
    },
    customerLandscape: {
      title: 'Customer Landscape & Program Environments',
      items: [
        'DRDO laboratories and R&D establishments (prototype and qualification-oriented builds)',
        'Defence Public Sector Units and integrators (production deliveries and multi-stakeholder acceptance)',
        'Ministry of Defence / Government programs (compliance-led documentation and controlled communication)',
        'Foreign government / armed forces programs (export-oriented execution where permitted)'
      ]
    },
    ndaDisclosure: 'Program details are often NDA-bound; customer references and case summaries can be shared during technical discussions, subject to approvals.'
  },
  contact: {
    title: 'Engage with HADEN',
    subtitle: 'Connect with our engineering team',
    factoryAddress: 'Factory Address',
    fromAirport: 'From Airport',
    transportation: 'Transportation',
    transportationText: 'HADEN can arrange airport pickup upon request',
    email: 'Email',
    location: 'Location',
    buttons: {
      requestQuote: 'Request Quote',
      technicalDiscussion: 'Technical Discussion',
      factoryVisit: 'Factory Visit',
      uploadRfq: 'Upload RFQ'
    }
  },
  nav: {
    solutions: 'Solutions',
    approach: 'Approach',
    expertise: 'Expertise',
    aboutUs: 'About Us',
    capabilities: 'Capabilities',
    process: 'Process',
    contact: 'Contact',
    requestQuote: 'Request Quote',
    support: 'Support',
    language: 'Language',
    loginTo: 'Login to',
    atlasLogin: 'Login to HADEN Atlas',
    atlasComingSoon: 'Coming Soon — Portal activation in progress'
  },
  supportComingSoon: {
    title: 'Service & Support Center',
    comingSoon: 'Coming Soon',
    intro: 'HADEN has delivered 50+ engineered systems across multiple defense programs. This portal will provide our customers with:',
    footerNote: 'This portal is currently under development. For immediate support, please use the button below to submit a service request.',
    serviceRequestCta: 'Submit Service Request',
    features: [
      { icon: 'tool', title: 'Service Request Submission', description: 'Log maintenance or support tickets' },
      { icon: 'clipboard', title: 'Complaint Lodging', description: 'Report issues with delivered systems' },
      { icon: 'truck', title: 'Tracking History', description: 'View status of your service requests' },
      { icon: 'file', title: 'Documentation Access', description: 'Retrieve system manuals & certificates' },
      { icon: 'bell', title: 'Maintenance Alerts', description: 'Scheduled service notifications' }
    ]
  },
  cta: {
    requestQuote: 'Request Quote',
    technicalDiscussion: 'Technical Discussion',
    factoryVisit: 'Factory Visit',
    uploadRfq: 'Upload RFQ'
  },
  regions: {
    title: 'Region & Language',
    northAmerica: 'North America',
    asia: 'Asia',
    europe: 'Europe',
    middleEast: 'Middle East'
  },
  countries: {
    usa: 'United States',
    india: 'India',
    japan: 'Japan',
    israel: 'Israel'
  },
  footer: {
    company: 'Precision Engineering for Aerospace & Defense',
    contact: 'Contact',
    admin: 'Admin',
    adminLogin: 'Admin Login',
    copyright: '© 2025 HADEN Systems Private Limited. All rights reserved.'
  },
  whyPartner: {
    title: 'Why Partner with HADEN',
    subtitle: 'What sets us apart in defence system delivery',
    tiles: [
      {
        title: 'Single-Source Accountability',
        description: 'Design to delivery under one roof — no multi-vendor coordination delays or finger-pointing.'
      },
      {
        title: 'First-Time FAT Clearance Focus',
        description: 'Evidence packs and test planning structured for acceptance closure, not rework cycles.'
      },
      {
        title: 'Defence-Ready Documentation',
        description: 'Requirement traceability, as-built records, and witness testing support built into every program.'
      },
      {
        title: 'Responsive Engineering Support',
        description: 'Direct access to engineering team for queries, changes, and field support — not a service desk.'
      }
    ]
  },
  contactLabels: {
    distance: 'Distance',
    travelTime: 'Travel Time',
    route: 'Route',
    nearby: 'Nearby',
    transportationText: 'HADEN can arrange airport pickup upon request'
  },
  confidentialityFootnote: 'Programs executed under confidentiality norms; references shared on request subject to permissions.',
  strategicSectors: {
    title: "Serving India's Strategic Sectors",
    subtitle: 'HADEN delivers mission-critical systems across:',
    sectors: [
      { id: 'radar', icon: 'radar', name: 'Ground Radar & Surveillance' },
      { id: 'naval', icon: 'naval', name: 'Naval Platforms & Shipboard' },
      { id: 'airborne', icon: 'airborne', name: 'Airborne & Support Equipment' },
      { id: 'missile', icon: 'missile', name: 'Missile Ground Support' },
      { id: 'test', icon: 'test', name: 'Test & Evaluation Facilities' },
      { id: 'psu', icon: 'psu', name: 'Defence PSU Programs' }
    ]
  }
};

// Build the resources object with all translations
const resources = {
  en: {
    translation: enContent
  },
  hi: {
    translation: {
      // Merge content from JSON file first
      ...hiContent,
      // Then override with specific translations
      nav: {
        ...hiContent.nav,
        solutions: 'समाधान',
        approach: 'दृष्टिकोण',
        expertise: 'विशेषज्ञता',
        aboutUs: 'हमारे बारे में',
        capabilities: 'क्षमताएं',
        process: 'प्रक्रिया',
        contact: 'संपर्क करें',
        requestQuote: 'कोटेशन का अनुरोध करें',
        support: 'समर्थन',
        language: 'भाषा',
        loginTo: 'लॉगिन करें',
        atlasLogin: 'HADEN Atlas में लॉगिन करें',
        atlasComingSoon: 'जल्द आ रहा है — पोर्टल सक्रियण प्रगति पर है'
      },
      cta: {
        requestQuote: 'उद्धरण अनुरोध',
        technicalDiscussion: 'तकनीकी चर्चा',
        factoryVisit: 'कारखाना यात्रा',
        uploadRfq: 'RFQ अपलोड करें'
      },
      regions: {
        title: 'क्षेत्र और भाषा',
        northAmerica: 'उत्तरी अमेरिका',
        asia: 'एशिया',
        europe: 'यूरोप',
        middleEast: 'मध्य पूर्व'
      },
      countries: {
        usa: 'संयुक्त राज्य अमेरिका',
        india: 'भारत',
        japan: 'जापान',
        israel: 'इज़राइल'
      },
      footer: {
        company: 'एयरोस्पेस और रक्षा के लिए सटीक इंजीनियरिंग',
        contact: 'संपर्क करें',
        admin: 'व्यवस्थापक',
        adminLogin: 'व्यवस्थापक लॉगिन',
        copyright: '© 2025 HADEN Systems Private Limited. सर्वाधिकार सुरक्षित।'
      },
      whyPartner: {
        title: 'HADEN के साथ साझेदारी क्यों करें',
        subtitle: 'रक्षा प्रणाली वितरण में हमें अलग बनाने वाली बातें',
        tiles: [
          {
            title: 'एकल-स्रोत जवाबदेही',
            description: 'डिज़ाइन से डिलीवरी तक एक छत के नीचे — कोई बहु-विक्रेता समन्वय विलंब या उंगली-दिखाना नहीं।'
          },
          {
            title: 'पहली बार FAT क्लियरेंस फोकस',
            description: 'साक्ष्य पैक और परीक्षण योजना स्वीकृति समापन के लिए संरचित, पुनर्कार्य चक्रों के लिए नहीं।'
          },
          {
            title: 'रक्षा-तैयार दस्तावेज़ीकरण',
            description: 'आवश्यकता ट्रेसेबिलिटी, एज़-बिल्ट रिकॉर्ड, और साक्षी परीक्षण समर्थन हर कार्यक्रम में निर्मित।'
          },
          {
            title: 'प्रतिक्रियाशील इंजीनियरिंग समर्थन',
            description: 'प्रश्नों, परिवर्तनों और फील्ड सपोर्ट के लिए इंजीनियरिंग टीम तक सीधी पहुंच — सर्विस डेस्क नहीं।'
          }
        ]
      },
      evidencePack: {
        ...hiContent.evidencePack,
        title: 'सामान्य साक्ष्य पैक',
        label: 'सामान्य स्वीकृति साक्ष्य पैक (कार्यक्रम निर्भर):',
        microLine: 'साक्ष्य संवेदनशील कार्यक्रम विवरणों को उजागर किए बिना स्वीकृति समापन का समर्थन करने के लिए संरचित है।'
      },
      contactLabels: {
        distance: 'दूरी',
        travelTime: 'यात्रा समय',
        route: 'मार्ग',
        nearby: 'निकट',
        transportationText: 'HADEN अनुरोध पर हवाई अड्डे से पिकअप की व्यवस्था कर सकता है'
      },
      confidentialityFootnote: 'कार्यक्रम गोपनीयता मानदंडों के तहत निष्पादित; अनुमतियों के अधीन अनुरोध पर संदर्भ साझा किए जाते हैं।',
      milGradeLayer: {
        ...hiContent.milGradeLayer,
        footnote: 'सटीक मानक संरेखण और सत्यापन दायरा ग्राहक कार्यक्रम आवश्यकताओं और संविदात्मक दस्तावेज़ीकरण पर निर्भर करता है।'
      },
      coreProductClasses: {
        ...hiContent.coreProductClasses,
        deliveredProductsLabel: 'वितरित उत्पाद उदाहरण',
        deployedForPrefix: 'के लिए तैनात',
        deliveredProducts: [
          ['शेल्टर कूलिंग यूनिट (SCU)', 'लिक्विड कूलिंग यूनिट (LCU)', 'एयर वेंटिलेशन और कंडीशनिंग सिस्टम', 'इलेक्ट्रॉनिक्स कूलिंग पैकेज'],
          ['प्रेशर लाइन एसेंबली', 'फ्लो रेगुलेशन स्किड', 'लिक्विड-टू-लिक्विड हीट एक्सचेंजर', 'कूलेंट डिस्ट्रीब्यूशन नेटवर्क'],
          ['पावर डिस्ट्रीब्यूशन यूनिट (PDU)', 'मोशन कंट्रोल पैनल', 'स्विचगियर और प्रोटेक्शन सिस्टम', 'डिफेंस-ग्रेड वायरिंग हार्नेस'],
          ['गैस रेगुलेशन एसेंबली', 'प्रेशर रिडक्शन स्टेशन', 'पर्ज और चार्जिंग सिस्टम', 'सेफ्टी इंटरलॉक पैनल']
        ],
        deployedFor: [
          'रडार सिस्टम, कमांड पोस्ट, मोबाइल शेल्टर और शिपबोर्ड इलेक्ट्रॉनिक्स के लिए तैनात',
          'नेवल प्लेटफॉर्म, ग्राउंड सपोर्ट सिस्टम और टेस्ट फैसिलिटीज के लिए तैनात',
          'रडार वैन, मोबाइल लॉन्चर, शिपबोर्ड सिस्टम और ग्राउंड स्टेशन के लिए तैनात',
          'मिसाइल सपोर्ट, टेस्ट स्टैंड, प्रोपल्शन सिस्टम और कैलिब्रेशन रिग के लिए तैनात'
        ]
      },
      strategicSectors: {
        title: 'भारत के रणनीतिक क्षेत्रों की सेवा में',
        subtitle: 'HADEN मिशन-क्रिटिकल सिस्टम प्रदान करता है:',
        sectors: [
          { id: 'radar', icon: 'radar', name: 'ग्राउंड रडार और निगरानी' },
          { id: 'naval', icon: 'naval', name: 'नौसेना प्लेटफॉर्म और शिपबोर्ड' },
          { id: 'airborne', icon: 'airborne', name: 'एयरबोर्न और सहायक उपकरण' },
          { id: 'missile', icon: 'missile', name: 'मिसाइल ग्राउंड सपोर्ट' },
          { id: 'test', icon: 'test', name: 'परीक्षण और मूल्यांकन सुविधाएं' },
          { id: 'psu', icon: 'psu', name: 'रक्षा PSU कार्यक्रम' }
        ]
      }
    }
  },
  he: {
    translation: {
      // Merge content from JSON file first
      ...heContent,
      // Then override with specific translations
      nav: {
        ...heContent.nav,
        solutions: 'פתרונות',
        approach: 'גישה',
        expertise: 'מומחיות',
        aboutUs: 'אודותינו',
        capabilities: 'יכולות',
        process: 'תהליך',
        contact: 'צור קשר',
        requestQuote: 'בקש הצעת מחיר',
        support: 'תמיכה',
        language: 'שפה',
        loginTo: 'התחבר אל',
        atlasLogin: 'התחבר ל-HADEN Atlas',
        atlasComingSoon: 'בקרוב — הפעלת פורטל בתהליך'
      },
      cta: {
        requestQuote: 'בקש הצעת מחיר',
        technicalDiscussion: 'דיון טכני',
        factoryVisit: 'ביקור במפעל',
        uploadRfq: 'העלה RFQ'
      },
      regions: {
        title: 'אזור ושפה',
        northAmerica: 'צפון אמריקה',
        asia: 'אסיה',
        europe: 'אירופה',
        middleEast: 'המזרח התיכון'
      },
      countries: {
        usa: 'ארצות הברית',
        india: 'הודו',
        japan: 'יפן',
        israel: 'ישראל'
      },
      footer: {
        company: 'הנדסת דיוק לתעופה וחלל והגנה',
        contact: 'צור קשר',
        admin: 'מנהל',
        adminLogin: 'כניסת מנהל',
        copyright: '© 2025 HADEN Systems Private Limited. כל הזכויות שמורות.'
      },
      whyPartner: {
        title: 'למה לשתף פעולה עם HADEN',
        subtitle: 'מה מייחד אותנו במסירת מערכות הגנה',
        tiles: [
          {
            title: 'אחריות ממקור יחיד',
            description: 'מעיצוב למסירה תחת קורת גג אחת — ללא עיכובי תיאום רב-ספקים או הטחת האשמות.'
          },
          {
            title: 'מיקוד באישור FAT ראשון',
            description: 'חבילות ראיות ותכנון בדיקות מובנים לסגירת קבלה, לא מחזורי עיבוד מחדש.'
          },
          {
            title: 'תיעוד מוכן להגנה',
            description: 'עקיבות דרישות, רשומות as-built, ותמיכה בבדיקות עדים מובנים בכל תוכנית.'
          },
          {
            title: 'תמיכת הנדסה מגיבה',
            description: 'גישה ישירה לצוות ההנדסה לשאילתות, שינויים ותמיכה בשטח — לא דלפק שירות.'
          }
        ]
      },
      evidencePack: {
        ...heContent.evidencePack,
        title: 'חבילת ראיות טיפוסית',
        label: 'חבילת ראיות קבלה טיפוסית (תלוי תוכנית):',
        microLine: 'ראיות מובנות לתמיכה בסגירת קבלה מבלי לחשוף פרטי תוכנית רגישים.'
      },
      contactLabels: {
        distance: 'מרחק',
        travelTime: 'זמן נסיעה',
        route: 'מסלול',
        nearby: 'בקרבת מקום',
        transportationText: 'HADEN יכול לארגן איסוף משדה התעופה לפי בקשה'
      },
      confidentialityFootnote: 'תוכניות מבוצעות תחת נורמות סודיות; הפניות משותפות לפי בקשה בכפוף לאישורים.',
      milGradeLayer: {
        ...heContent.milGradeLayer,
        footnote: 'התאמת תקנים מדויקת והיקף אימות תלויים בדרישות תוכנית הלקוח ובתיעוד החוזי.'
      },
      coreProductClasses: {
        ...heContent.coreProductClasses,
        deliveredProductsLabel: 'דוגמאות מוצרים שסופקו',
        deployedForPrefix: 'מותקן עבור',
        deliveredProducts: [
          ['יחידות קירור מקלט (SCU)', 'יחידות קירור נוזלי (LCU)', 'מערכות אוורור ומיזוג אוויר', 'חבילות קירור אלקטרוניקה'],
          ['מכלולי קו לחץ', 'מערכות ויסות זרימה', 'מחליפי חום נוזל לנוזל', 'רשתות הפצת קירור'],
          ['יחידות הפצת חשמל (PDU)', 'לוחות בקרת תנועה', 'מערכות מיתוג והגנה', 'רתמות חיווט צבאיות'],
          ['מכלולי ויסות גז', 'תחנות הפחתת לחץ', 'מערכות שטיפה וטעינה', 'לוחות נעילת בטיחות']
        ],
        deployedFor: [
          'מותקן עבור מערכות רדאר, עמדות פיקוד, מקלטים ניידים ואלקטרוניקה ספינתית',
          'מותקן עבור פלטפורמות ימיות, מערכות תמיכה קרקעיות ומתקני בדיקה',
          'מותקן עבור משאיות רדאר, משגרים ניידים, מערכות ספינתיות ותחנות קרקע',
          'מותקן עבור תמיכת טילים, מתקני בדיקה, מערכות הנעה ומתקני כיול'
        ]
      },
      strategicSectors: {
        title: 'משרתים את המגזרים האסטרטגיים של הודו',
        subtitle: 'HADEN מספקת מערכות קריטיות למשימה:',
        sectors: [
          { id: 'radar', icon: 'radar', name: 'רדאר קרקעי ומעקב' },
          { id: 'naval', icon: 'naval', name: 'פלטפורמות ימיות וספינות' },
          { id: 'airborne', icon: 'airborne', name: 'ציוד אווירי ותמיכה' },
          { id: 'missile', icon: 'missile', name: 'תמיכה קרקעית לטילים' },
          { id: 'test', icon: 'test', name: 'מתקני בדיקה והערכה' },
          { id: 'psu', icon: 'psu', name: 'תוכניות PSU הגנה' }
        ]
      }
    }
  },
  ja: {
    translation: {
      // Merge content from JSON file first
      ...jaContent,
      // Then override with specific translations
      nav: {
        ...jaContent.nav,
        solutions: 'ソリューション',
        approach: 'アプローチ',
        expertise: '専門知識',
        aboutUs: '会社概要',
        capabilities: '機能',
        process: 'プロセス',
        contact: 'お問い合わせ',
        requestQuote: '見積もりを依頼',
        support: 'サポート',
        language: '言語',
        loginTo: 'ログイン',
        atlasLogin: 'HADEN Atlasにログイン',
        atlasComingSoon: '近日公開 — ポータル有効化進行中'
      },
      cta: {
        requestQuote: '見積もりを依頼',
        technicalDiscussion: '技術ディスカッション',
        factoryVisit: '工場見学',
        uploadRfq: 'RFQをアップロード'
      },
      regions: {
        title: '地域と言語',
        northAmerica: '北米',
        asia: 'アジア',
        europe: 'ヨーロッパ',
        middleEast: '中東'
      },
      countries: {
        usa: 'アメリカ合衆国',
        india: 'インド',
        japan: '日本',
        israel: 'イスラエル'
      },
      footer: {
        company: '航空宇宙・防衛向け精密エンジニアリング',
        contact: 'お問い合わせ',
        admin: '管理者',
        adminLogin: '管理者ログイン',
        copyright: '© 2025 HADEN Systems Private Limited. 全著作権所有。'
      },
      whyPartner: {
        title: 'HADENとのパートナーシップの理由',
        subtitle: '防衛システム納入において当社を際立たせるもの',
        tiles: [
          {
            title: 'シングルソース責任',
            description: '設計から納入まで一つの屋根の下で — マルチベンダー調整の遅延や責任転嫁なし。'
          },
          {
            title: '初回FAT合格フォーカス',
            description: '証拠パックとテスト計画は、やり直しサイクルではなく、受け入れクロージャのために構成。'
          },
          {
            title: '防衛対応ドキュメンテーション',
            description: '要件トレーサビリティ、アズビルト記録、立会テストサポートがすべてのプログラムに組み込み。'
          },
          {
            title: 'レスポンシブエンジニアリングサポート',
            description: '質問、変更、フィールドサポートのためのエンジニアリングチームへの直接アクセス — サービスデスクではありません。'
          }
        ]
      },
      evidencePack: {
        ...jaContent.evidencePack,
        title: '典型的な証拠パック',
        label: '典型的な受け入れ証拠パック（プログラム依存）:',
        microLine: '証拠は機密性の高いプログラム詳細を公開せずに受け入れクロージャをサポートするように構成されています。'
      },
      contactLabels: {
        distance: '距離',
        travelTime: '所要時間',
        route: 'ルート',
        nearby: '近隣',
        transportationText: 'HADENはリクエストに応じて空港送迎を手配できます'
      },
      confidentialityFootnote: 'プログラムは機密保持規範に基づいて実行されます。承認を条件として、リクエストに応じて参照を共有します。',
      milGradeLayer: {
        ...jaContent.milGradeLayer,
        footnote: '正確な規格の整合性と検証範囲は、顧客プログラムの要件と契約文書に依存します。'
      },
      coreProductClasses: {
        ...jaContent.coreProductClasses,
        deliveredProductsLabel: '納入製品例',
        deployedForPrefix: '導入先',
        deliveredProducts: [
          ['シェルター冷却ユニット (SCU)', '液体冷却ユニット (LCU)', '空調換気システム', '電子機器冷却パッケージ'],
          ['圧力ラインアセンブリ', '流量制御スキッド', '液-液熱交換器', '冷却材配管ネットワーク'],
          ['配電ユニット (PDU)', 'モーションコントロールパネル', 'スイッチギアと保護システム', '防衛グレード配線ハーネス'],
          ['ガス調整アセンブリ', '減圧ステーション', 'パージ・チャージシステム', '安全インターロックパネル']
        ],
        deployedFor: [
          'レーダーシステム、指揮所、移動式シェルター、艦載電子機器に導入',
          '海軍プラットフォーム、地上支援システム、試験施設に導入',
          'レーダー車両、移動式発射装置、艦載システム、地上局に導入',
          'ミサイル支援、試験台、推進システム、校正リグに導入'
        ]
      },
      strategicSectors: {
        title: 'インドの戦略的セクターに貢献',
        subtitle: 'HADENはミッションクリティカルなシステムを提供:',
        sectors: [
          { id: 'radar', icon: 'radar', name: '地上レーダーと監視' },
          { id: 'naval', icon: 'naval', name: '海軍プラットフォームと艦船' },
          { id: 'airborne', icon: 'airborne', name: '航空機と支援装備' },
          { id: 'missile', icon: 'missile', name: 'ミサイル地上支援' },
          { id: 'test', icon: 'test', name: '試験と評価施設' },
          { id: 'psu', icon: 'psu', name: '防衛PSUプログラム' }
        ]
      }
    }
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('language') || 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    }
  });

export default i18n;
